export interface DiagramLayoutInput {
  viewBoxWidth: number;
  viewBoxHeight: number;
  viewportWidth: number;
  horizontalPadding: number;
  verticalPadding: number;
  minimumViewportHeight: number;
  maximumViewportHeight: number;
  smallestTextUnit: number;
  minimumRenderedTextPx?: number;
  maximumRenderedDimensionPx?: number;
}

export interface DiagramLayout {
  baseScale: number;
  viewportHeight: number;
  renderedWidth: number;
  renderedHeight: number;
  needsHorizontalScroll: boolean;
  needsVerticalScroll: boolean;
}

const clamp = (value: number, minimum: number, maximum: number): number => Math.min(maximum, Math.max(minimum, value));

export function computeDiagramLayout(input: DiagramLayoutInput): DiagramLayout {
  const width = Math.max(1, input.viewBoxWidth);
  const height = Math.max(1, input.viewBoxHeight);
  const usableWidth = Math.max(1, input.viewportWidth - input.horizontalPadding);
  const widthFit = usableWidth / width;
  const viewportHeight = clamp(
    height * widthFit + input.verticalPadding,
    input.minimumViewportHeight,
    input.maximumViewportHeight,
  );
  const usableHeight = Math.max(1, viewportHeight - input.verticalPadding);
  const containScale = Math.min(widthFit, usableHeight / height);
  const readableScale = (input.minimumRenderedTextPx ?? 13) / Math.max(1, input.smallestTextUnit);
  const dimensionCap = (input.maximumRenderedDimensionPx ?? 2_400) / Math.max(width, height);
  const baseScale = Math.max(containScale, Math.min(readableScale, Math.max(containScale, dimensionCap)));
  const renderedWidth = width * baseScale;
  const renderedHeight = height * baseScale;
  return {
    baseScale,
    viewportHeight,
    renderedWidth,
    renderedHeight,
    needsHorizontalScroll: renderedWidth > usableWidth + 1,
    needsVerticalScroll: renderedHeight > usableHeight + 1,
  };
}

interface DiagramViewportControllerOptions {
  viewport: HTMLElement;
  diagram: HTMLElement;
  svg: SVGSVGElement;
  zoomIn: HTMLButtonElement;
  zoomOut: HTMLButtonElement;
  zoomReset: HTMLButtonElement;
}

function numericStyle(element: Element, property: string): number {
  return Number.parseFloat(getComputedStyle(element).getPropertyValue(property)) || 0;
}

function viewBoxSize(svg: SVGSVGElement): { width: number; height: number } {
  const box = svg.viewBox.baseVal;
  if (box.width > 0 && box.height > 0) return { width: box.width, height: box.height };
  const bounds = svg.getBBox();
  return { width: Math.max(1, bounds.width), height: Math.max(1, bounds.height) };
}

function smallestTextUnit(svg: SVGSVGElement): number {
  const sizes = [...svg.querySelectorAll<SVGTextElement>("text")]
    .map((node) => Number.parseFloat(getComputedStyle(node).fontSize))
    .filter((size) => Number.isFinite(size) && size > 0);
  return sizes.length > 0 ? Math.min(...sizes) : 16;
}

export function createDiagramViewportController(options: DiagramViewportControllerOptions): () => void {
  const { viewport, diagram, svg, zoomIn, zoomOut, zoomReset } = options;
  const size = viewBoxSize(svg);
  const textUnit = smallestTextUnit(svg);
  let baseScale = 1;
  let zoomFactor = 1;
  let lastWidth = -1;
  let frame = 0;

  viewport.tabIndex = 0;
  viewport.setAttribute("role", "region");
  viewport.setAttribute("aria-label", "Scrollable Mermaid diagram");

  const contentCenter = (): { x: number; y: number } => ({
    x: viewport.scrollWidth > 0 ? (viewport.scrollLeft + viewport.clientWidth / 2) / viewport.scrollWidth : 0.5,
    y: viewport.scrollHeight > 0 ? (viewport.scrollTop + viewport.clientHeight / 2) / viewport.scrollHeight : 0.5,
  });

  const restoreCenter = (center: { x: number; y: number }): void => {
    viewport.scrollLeft = center.x * viewport.scrollWidth - viewport.clientWidth / 2;
    viewport.scrollTop = center.y * viewport.scrollHeight - viewport.clientHeight / 2;
  };

  const apply = (preserveCenter: boolean): void => {
    const center = preserveCenter ? contentCenter() : { x: 0.5, y: 0.5 };
    const horizontalPadding = numericStyle(viewport, "padding-left") + numericStyle(viewport, "padding-right");
    const verticalPadding = numericStyle(viewport, "padding-top") + numericStyle(viewport, "padding-bottom");
    const mobile = matchMedia("(max-width: 560px)").matches;
    const layout = computeDiagramLayout({
      viewBoxWidth: size.width,
      viewBoxHeight: size.height,
      viewportWidth: viewport.clientWidth,
      horizontalPadding,
      verticalPadding,
      minimumViewportHeight: mobile ? 300 : 320,
      maximumViewportHeight: mobile ? 540 : 680,
      smallestTextUnit: textUnit,
    });
    baseScale = layout.baseScale;
    viewport.style.height = `${layout.viewportHeight}px`;
    const scale = baseScale * zoomFactor;
    const renderedWidth = size.width * scale;
    const renderedHeight = size.height * scale;
    const usableWidth = Math.max(0, viewport.clientWidth - horizontalPadding);
    const usableHeight = Math.max(0, layout.viewportHeight - verticalPadding);
    diagram.style.width = `${renderedWidth}px`;
    diagram.style.height = `${renderedHeight}px`;
    diagram.style.marginLeft = `${Math.max(0, (usableWidth - renderedWidth) / 2)}px`;
    diagram.style.marginTop = `${Math.max(0, (usableHeight - renderedHeight) / 2)}px`;
    svg.style.width = "100%";
    svg.style.height = "100%";
    zoomReset.textContent = `${Math.round(zoomFactor * 100)}%`;
    requestAnimationFrame(() => restoreCenter(center));
  };

  const scheduleLayout = (preserveCenter = true): void => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => apply(preserveCenter));
  };

  const zoom = (delta: number): void => {
    zoomFactor = clamp(Math.round((zoomFactor + delta) * 10) / 10, 0.5, 2.5);
    apply(true);
  };
  zoomIn.addEventListener("click", () => zoom(0.1));
  zoomOut.addEventListener("click", () => zoom(-0.1));
  zoomReset.addEventListener("click", () => { zoomFactor = 1; apply(false); });

  let pointer: { id: number; x: number; y: number; left: number; top: number } | null = null;
  viewport.addEventListener("pointerdown", (event) => {
    if ((event.target as HTMLElement).closest("button")) return;
    pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    viewport.setPointerCapture(event.pointerId); viewport.classList.add("is-panning");
  });
  viewport.addEventListener("pointermove", (event) => {
    if (!pointer || pointer.id !== event.pointerId) return;
    viewport.scrollLeft = pointer.left - (event.clientX - pointer.x);
    viewport.scrollTop = pointer.top - (event.clientY - pointer.y);
  });
  const stopPanning = (): void => { pointer = null; viewport.classList.remove("is-panning"); };
  viewport.addEventListener("pointerup", stopPanning);
  viewport.addEventListener("pointercancel", stopPanning);

  const observer = new ResizeObserver(() => {
    const width = viewport.clientWidth;
    if (Math.abs(width - lastWidth) < 1) return;
    lastWidth = width; scheduleLayout(true);
  });
  observer.observe(viewport);
  apply(false);
  return () => { observer.disconnect(); cancelAnimationFrame(frame); };
}

